import axios from "axios";
import { getLocalStorage, getSessionStorage } from "../sessionAndLocalStorage";
import { SourceSystemNames } from "../config/sourceSystemNames";
import { convertValueToArray } from "../helpers/questionsAndAnswers";
require("es6-promise").polyfill();

axios.defaults.baseURL = process.env.REACT_APP_BASE_URL;
axios.defaults.validateStatus = (status) => {
  return status <= 500;
};

axios.interceptors.request.use(
  (config) => {
    const appraisalSessionId = getSessionStorage("appraisalSessionId");
    if (!config.headers.Authorization) {
      const abycAuthToken = getLocalStorage(
        `_abyc_authToken_${appraisalSessionId}`
      );

      if (abycAuthToken) {
        config.headers.Authorization = `Bearer ${abycAuthToken}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export async function fetchAppraisal(appraisalSessionId) {
  const response = await axios({
    method: "GET",
    url: `/v3/Appraisal/${appraisalSessionId}`
  });

  return response.data;
}
export async function fetchSavedQuestionAndAnswersSet(appraisalSessionId) {
  const response = await axios({
    method: "GET",
    url: `/v3/Appraisal/${appraisalSessionId}/QuestionsAndAnswers/Answered`
  });
  
  return response.data;
}

export const fetchVehicle = async ({ registration, appraisalSessionId }) => {
  const response = await axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/Vehicle`,
    data: {
      registration
    }
  });

  return response.data;
};

export async function patchVehicle({ mileage, appraisalSessionId }) {
  const response = await axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/Vehicle/Mileage`,
    data: {
      mileage
    }
  });
  return response.data;
}

export async function fetchQuestions(appraisalSessionId) {
  const response = await axios({
    method: "GET",
    url: `/v4/Appraisal/${appraisalSessionId}/QuestionsAndAnswers`
  });

  return response.data;
}

export async function fetchFileStackKeys(appraisalSessionId) {
  const response = await axios({
    method: "GET",
    url: `/v1/ImageSecurity/${appraisalSessionId}/PickerPolicy`
  });

  return response.data;
}

export async function postImages({ images, appraisalSessionId }) {
  const response = await axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/Image`,
    data: { images }
  });

  return response.data;
}

export async function deleteImage({ imageIdentifier, appraisalSessionId }) {
  const response = await axios({
    method: "DELETE",
    url: `/v1/Appraisal/${appraisalSessionId}/Image/${imageIdentifier}`
  });

  return response.data;
}

export async function fetchValuation({
  questionsAndAnswers,
  appraisalSessionId,
  valuationAccuracy
}) {
  const response = await axios({
    method: "POST",
    url: `/v3/Appraisal/${appraisalSessionId}/Valuation`,
    data: {
      questionsAndAnswers: convertValueToArray(questionsAndAnswers),
      valuationAccuracy
    }
  });

  return response.data;
}

export function completeAppraisal(appraisalSessionId) {
  return axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/Complete`
  });
}

export function completeReappraisal(appraisalSessionId) {
  return axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/Reappraisal/Complete`
  });
}

export function initialise(sourceSystemName = "default") {
  const auth_tokens = {
    default: process.env.REACT_APP_AUTH_TOKEN,
    [SourceSystemNames.SalesCNP]: process.env.REACT_APP_AUTH_TOKEN_CNP,
    [SourceSystemNames.SalesOffice]:
      process.env.REACT_APP_AUTH_TOKEN_PARTEX_BRANCH,
    [SourceSystemNames.SalesOfficeCustomer]:
      process.env.REACT_APP_AUTH_TOKEN_PARTEX_CUSTOMER
  };
  const source_system_IDs = {
    default: process.env.REACT_APP_SOURCE_SYSTEM_ID,
    [SourceSystemNames.SalesCNP]: process.env.REACT_APP_SOURCE_SYSTEM_ID_CNP,
    [SourceSystemNames.SalesOffice]:
      process.env.REACT_APP_SOURCE_SYSTEM_ID_PARTEX_BRANCH,
    [SourceSystemNames.SalesOfficeCustomer]:
      process.env.REACT_APP_SOURCE_SYSTEM_ID_PARTEX_CUSTOMER
  };
  const AUTH_TOKEN = auth_tokens[sourceSystemName];
  const SOURCE_SYSTEM_ID = source_system_IDs[sourceSystemName];

  return axios({
    method: "POST",
    url: `/v1/Appraisal`,
    headers: {
      Authorization: `Bearer ${AUTH_TOKEN}`,
      sourceSystemId: SOURCE_SYSTEM_ID
    },
    data: {
      sourceSystemReferenceId: "string",
      sourceSystemBranchId: "string",
      employeeId: 1
    }
  });
}

export function initialiseForPhotos() {
  const AUTH_TOKEN = process.env.REACT_APP_PHOTOS_TEST_TOKEN;
  const SOURCE_SYSTEM_ID = process.env.REACT_APP_PHOTOS_TEST_ID;

  return axios({
    method: "POST",
    url: `/v1/Appraisal`,
    headers: {
      Authorization: `Bearer ${AUTH_TOKEN}`,
      sourceSystemId: SOURCE_SYSTEM_ID
    },
    data: {
      sourceSystemReferenceId: "string",
      sourceSystemBranchId: "string",
      employeeId: 1
    }
  });
}

export const fetchVehicleLookupMake = async ({ appraisalSessionId }) => {
  const response = await axios({
    method: "GET",
    url: `v1/appraisal/${appraisalSessionId}/VehicleLookup/Manufacturers`
  });
  return response.data;
};

export const fetchVehicleLookupModel = async ({ make, appraisalSessionId }) => {
  const response = await axios({
    method: "GET",
    url: `v1/appraisal/${appraisalSessionId}/VehicleLookup/Models?Manufacturer=${make}`
  });
  return response.data;
};

export const fetchVehicleLookupFuelTypes = async ({
  make,
  model,
  appraisalSessionId
}) => {
  const response = await axios({
    method: "GET",
    url: `v1/appraisal/${appraisalSessionId}/VehicleLookup/FuelTypes?Model=${model}&Manufacturer=${make}`
  });
  return response.data;
};

export const fetchVehicleLookupProductionYears = async ({
  make,
  model,
  fuelType,
  appraisalSessionId
}) => {
  const response = await axios({
    method: "GET",
    url: `v1/appraisal/${appraisalSessionId}/VehicleLookup/ProductionYears?Model=${model}&Manufacturer=${make}&FuelTypeId=${fuelType}`
  });
  return response.data;
};

export const fetchVehicleLookupDerivatives = async ({
  make,
  model,
  fuelType,
  dateOfRegistrationYear,
  registrationPlate,
  appraisalSessionId
}) => {
  const response = await axios({
    method: "GET",
    url: `v1/appraisal/${appraisalSessionId}/VehicleLookup/Derivatives?Model=${model}&Manufacturer=${make}&FuelTypeId=${fuelType}&DateOfRegistrationYear=${dateOfRegistrationYear}&RegistrationPlate=${registrationPlate}`
  });
  return response.data;
};

export const fetchManualVehicle = async ({ appraisalSessionId, payload }) => {
  const response = await axios({
    method: "POST",
    url: `v1/Appraisal/${appraisalSessionId}/ManualVehicle`,
    data: payload
  });
  return response.data;
};

export const fetchTellUsMore = async ({ appraisalSessionId, payload }) => {
  const response = await axios({
    method: "POST",
    url: `v1/Appraisal/${appraisalSessionId}/TellUsMore`,
    data: payload
  });
  return response.data;
};

export const postMultiMatchCapCode = async ({
  appraisalSessionId,
  selectedCapCode
}) => {
  const response = await axios({
    method: "POST",
    url: `/v1/Appraisal/${appraisalSessionId}/MultiMatchVehicle`,
    data: { capCode: selectedCapCode }
  });
  return response.data;
};
